import { Component, OnDestroy, OnInit } from '@angular/core';
import { AuthService, IdToken } from '@auth0/auth0-angular';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { AppState } from './viewbid/state/app-state';
import { TokenService } from './viewbid/shared/services/token.service';
import { ProfileService } from './viewbid/shared/services/profile.service';
import { PendingPickupService } from './viewbid/shared/services/pending-pickup.service';
import { SocketService } from './viewbid/shared/services/socket.service';
import { Title } from '@angular/platform-browser';
import { ViewportScroller } from '@angular/common';
import { Router, ActivatedRoute, NavigationEnd, Scroll } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, map, mergeMap, takeUntil } from 'rxjs/operators';
import { PendingInvoice } from './viewbid/shared/services/pending-pickup.service';
import { MetaTagsService } from './shared/services/meta-tags.service';
import { MasqueradeService } from './viewbid/shared/services/masquerade.service';
import { TokenStorageService } from 'viewbid-shared-lib';
import { PickupForfeitureModalComponent } from './viewbid/shared/components/pickup-forfeiture-modal/pickup-forfeiture-modal.component';
import { VbSnackbarService } from '@viewbid/ui';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    constructor(
        private translateService: TranslateService,
        private snackbarService: VbSnackbarService,
        public authService: AuthService,
        private store: Store<AppState>,
        private tokenService: TokenService,
        private profileService: ProfileService,
        private pendingPickupService: PendingPickupService,
        private socketService: SocketService,
        private titleService: Title,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private metaTagsService: MetaTagsService,
        public masqueradeService: MasqueradeService,
        private tokenStorageService: TokenStorageService,
        private viewportScroller: ViewportScroller,
    ) {
        this.initializeTranslations();
        // TODO: Check do we really need to add this subscriber
        this.profileService.subscribeToUserChanges();
        this.subscribeToAuthenticationStatus();
    }

    ngOnInit(): void {
        this.handleRouterScrolling();

        // Initialize masquerade state and handle user profile
        this.masqueradeService.isMasquerading$.subscribe(
            (isMasquerading: boolean) => {
                if (isMasquerading) {
                    console.log(
                        'Masquerade is active. Fetching masqueraded user details.',
                    );
                    // When masquerading, force profile service to refresh user details
                    this.profileService.forceProfileRefresh();
                }
            },
        );

        // Open the blocking modal whenever the pending-invoice list transitions
        // to non-empty, regardless of which code path populated it (guard,
        // socket, etc.). This avoids timing races where the guard finishes
        // before the API response lands.
        this.pendingPickupService.pendingInvoices$
            .pipe(
                filter(
                    (invoices: PendingInvoice[]): boolean =>
                        invoices.length > 0,
                ),
                takeUntil(this.destroy$),
            )
            .subscribe(() => {
                this.pendingPickupService.openModalIfPending(
                    PickupForfeitureModalComponent,
                );
            });

        // Listen for real-time pickup flag events. The sockets gateway unwraps
        // the SocketMessage envelope before emitting, so clients receive the
        // inner `{ action, data }` payload directly.
        this.socketService.resource$
            .pipe(takeUntil(this.destroy$))
            .subscribe((data: string) => {
                try {
                    const event: any = JSON.parse(data);
                    if (event.action === 'PICKUP_FLAGGED') {
                        const invoiceIds: string[] =
                            event.data?.invoiceIds ?? [];
                        this.pendingPickupService.addFromSocket(invoiceIds);
                    } else if (event.action === 'AUCTION_UPDATE') {
                        this.snackbarService.system(
                            this.translateService.instant(
                                'AuctionsPage.auctionSettingsUpdated',
                                { name: event.data?.name ?? '' },
                            ),
                        );
                    }
                } catch {
                    /* silent */
                }
            });

        // Listen to route changes and update the title dynamically
        this.router.events
            .pipe(
                filter((event: any) => event instanceof NavigationEnd),
                map(() => {
                    let route: ActivatedRoute = this.activatedRoute;
                    while (route.firstChild) {
                        route = route.firstChild;
                    }
                    return route;
                }),
                mergeMap((route: ActivatedRoute) => route.data),
            )
            .subscribe((event: any) => {
                if (event['title']) {
                    this.titleService.setTitle(event['title']);
                }
                if (!this.router.url.includes('/auctions/')) {
                    if (event['title']) {
                        this.metaTagsService.setTitleTag(event['title']);
                    }
                    this.metaTagsService.setDescriptionTag(
                        'Viewbid is a locally owned auction house in Halifax, Nova Scotia. Canada',
                    );
                    this.metaTagsService.setImageTag(
                        this.metaTagsService.getDefaultOgImage,
                    );
                }
                this.metaTagsService.setUrlTag(this.router.url);
            });
    }

    /**
     * Scrolling for completed navigations. The router is configured with
     * scrollPositionRestoration: 'disabled' (see AppRoutingModule), so the
     * stock RouterScroller no longer moves the viewport itself — anchors
     * excepted — and this handler reinstates only the behaviors worth having:
     *
     *  - back/forward → the offset the browser remembered for that entry;
     *  - a navigation whose path changed → top of the new page;
     *  - a navigation that only rewrote query params → nothing. The list
     *    pages sync page/sortBy into the URL mid-scroll, and the stock
     *    behavior yanked the viewport to the top on every such sync.
     */
    private handleRouterScrolling(): void {
        // The stock scroller sets this only when restoration is 'enabled';
        // without it the browser also restores on back/forward, racing the
        // Scroll-event restore below.
        this.viewportScroller.setHistoryScrollRestoration('manual');
        let lastPath: string | null = null;
        this.router.events
            .pipe(
                filter(
                    (event: unknown): event is Scroll =>
                        event instanceof Scroll,
                ),
                takeUntil(this.destroy$),
            )
            .subscribe((event: Scroll) => {
                // NavigationEnd carries urlAfterRedirects; a NavigationSkipped
                // url had no redirects to resolve, so it is already final.
                const url: string =
                    event.routerEvent instanceof NavigationEnd
                        ? event.routerEvent.urlAfterRedirects
                        : event.routerEvent.url;
                const path: string = url.split('#')[0].split('?')[0];
                const pathChanged: boolean = path !== lastPath;
                lastPath = path;
                if (event.position) {
                    // Back/forward.
                    this.viewportScroller.scrollToPosition(event.position);
                } else if (event.anchor) {
                    // The RouterScroller already scrolled to the anchor
                    // (anchorScrolling stays 'enabled').
                } else if (pathChanged) {
                    this.viewportScroller.scrollToPosition([0, 0]);
                }
            });
    }

    /**
     * Initialize language settings for the application.
     */
    private initializeTranslations(): void {
        this.translateService.addLangs(['en', 'fr']);
        this.translateService.setDefaultLang('en');
    }

    /**
     * Subscribe to authentication status and manage token refresh accordingly.
     */
    private subscribeToAuthenticationStatus(): void {
        this.authService.isAuthenticated$.subscribe(
            (isAuthenticated: boolean) => {
                if (isAuthenticated) {
                    this.authService.idTokenClaims$.subscribe(
                        (idToken: IdToken | null | undefined) => {
                            this.tokenService.checkTokenAndSetTimeout(idToken);
                        },
                    );
                }
            },
        );
    }

    ngOnDestroy(): void {
        // Clear the timeout to prevent it from running after the component is destroyed
        this.tokenService.clearTokenRefreshTimeout();
        this.destroy$.next();
        this.destroy$.complete();
    }
}
